<script setup lang="ts">
  import { ref, nextTick } from "vue";
  import { Trash2, Pencil, Check, ChevronDown, ChevronRight, GripVertical } from "lucide-vue-next";

  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";

  import { useSurveySectionsStore } from "@stores/survey/surveySections";

  interface Section {
    token: string;
    name: string;
    [key: string]: unknown;
  }

  interface Props {
    section: Section;
    sectionIndex?: number;
    isListReadOnly?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    sectionIndex: 0,
    isListReadOnly: false,
  });

  const edit = ref(false);
  const currentSectionName = ref("");
  const input = ref<HTMLInputElement | null>(null);

  const sectionsStore = useSurveySectionsStore();

  const { removeSection, setSectionName, toggleCollapsedSection, isCollapsedSection } = sectionsStore;

  const handleEditName = () => {
    currentSectionName.value = props.section.name;
    edit.value = !edit.value;
    nextTick(() => {
      input.value?.focus();
    });
  };

  const handleSetSectionName = () => {
    setSectionName(props.section, currentSectionName.value);
    edit.value = !edit.value;
  };

  const toggleCollapse = () => {
    toggleCollapsedSection(props.section);
  };
</script>

<template>
  <div
    class="group flex items-center gap-1.5 rounded-lg bg-muted py-1.5 pr-1.5 pl-1.5"
    :class="sectionIndex === 0 ? 'mb-1' : 'mt-3 mb-1'">
    <Button
      variant="ghost"
      size="icon-sm"
      class="size-6 shrink-0 text-foreground-subtle hover:bg-white hover:text-primary"
      @click="toggleCollapse">
      <ChevronRight
        v-if="isCollapsedSection(section.token)"
        class="size-3.5" />
      <ChevronDown
        v-else
        class="size-3.5" />
    </Button>

    <div
      v-if="!edit || isListReadOnly"
      class="flex-1 min-w-0 cursor-pointer"
      @click="toggleCollapse">
      <span class="block truncate text-xs font-semibold uppercase tracking-[0.06em] text-foreground-secondary">
        {{ section.name }}
      </span>
    </div>

    <Input
      v-else
      ref="input"
      v-model="currentSectionName"
      class="h-7 flex-1 text-xs"
      @blur="handleSetSectionName"
      @keyup.enter="handleSetSectionName" />

    <div
      v-if="!edit || isListReadOnly"
      class="flex shrink-0 items-center gap-0.5">
      <div
        v-if="!isListReadOnly"
        class="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <span
          v-if="sectionIndex !== 0"
          class="drag-handle flex size-6 cursor-grab items-center justify-center text-muted-foreground hover:text-primary active:cursor-grabbing">
          <GripVertical class="size-3.5" />
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          class="size-6 text-muted-foreground hover:bg-white hover:text-primary"
          @click="handleEditName">
          <Pencil class="size-3" />
        </Button>
        <Button
          v-if="sectionIndex !== 0"
          variant="ghost"
          size="icon-sm"
          class="size-6 text-muted-foreground hover:bg-white hover:text-primary"
          @click="removeSection(section)">
          <Trash2 class="size-3" />
        </Button>
      </div>
    </div>

    <div
      v-else
      class="flex items-center gap-1 shrink-0">
      <Button
        variant="ghost"
        size="icon-sm"
        class="size-6"
        @click="handleSetSectionName">
        <Check class="size-3" />
      </Button>
    </div>
  </div>
</template>
